import { getFCMToken } from "firebase";
import { deleteToken, setToken, useApi } from "hooks/useApi";
import { AppDispatch } from "store";

const api = useApi();

export const logout = () => async (dispatch: AppDispatch) => {
  try {
    await api.post('/logout');
  } catch {
    // Revoke locally even if the request fails (e.g. network or already logged out)
  } finally {
    deleteToken();
    dispatch({ type: "LOGOUT" });
  }
};

export const isLoggedIn = () => async (dispatch: AppDispatch) => {
  try {
    const { data } = await api.get("/me");
    dispatch({
      type: "SET_USER_DATA",
      data: data?.user,
    });
    return data;
  } catch (err: any) {
    deleteToken();
    dispatch({ type: "LOGOUT" });
    // 401 is expected when not logged in — don't throw so callers don't log it as an error
    if (err.response?.status === 401) {
      return null;
    }
    throw err.response?.data;
  }
};

export const login =
  (data: { phone_number: string; password: string }) =>
  async (dispatch: AppDispatch) => {
    try {
      let fcm_token: string | undefined;
      try {
        fcm_token = await getFCMToken() ?? undefined;
      } catch {
        fcm_token = undefined;
      }
      const res = await api.post("/login", { ...data, fcm_token });
      if (res.data?.token) {
        setToken(res.data.token);
      }
      dispatch({
        type: "SET_USER_DATA",
        data: res.data?.user,
      });
      return res.data;
    } catch (err: any) {
      deleteToken();
      throw err.response?.data;
    }
  };

export const register = (data: any) => async (dispatch: AppDispatch) => {
  try {
    const res = await api.post("/user/complete-registration", data);
    dispatch({
      type: "SET_USER_DATA",
      data: res.data?.user,
    });
    return res.data;
  } catch (err: any) {
    throw err.response.data;
  }
};

export const completeShippingDetails =
  (data: {
    name: string;
    city: string;
    address: string;
    phone_number: string;
  }) =>
  async (dispatch: AppDispatch) => {
    try {
      const res = await api.post("/user/complete-shipping-details", data);
      dispatch({
        type: "SET_USER_DATA",
        data: res.data.user,
      });
      return res.data;
    } catch (err: any) {
      throw err.response.data;
    }
  };

export const sendOtp =
  (phone_number: string) => async () => {
    await api.post("/otp/send", { phone_number });
  };

export const verifyOtp =
  (phone_number: string, code: string) =>
  async (dispatch: AppDispatch) => {
    const { data } = await api.post("/otp/verify", { phone_number, code });
    if (data?.token) {
      setToken(data.token);
    }
    dispatch({ type: "SET_USER_DATA", data: data?.user });
    return data;
  };

export const otpLogin =
  ({
    accessToken,
    phone_number,
  }: {
    accessToken: string;
    phone_number?: string;
  }) =>
  async (dispatch: AppDispatch) => {
    return new Promise(async (resolve, reject) => {
      try {
        const payload: { opt_id_token: string; phone_number?: string } = {
          opt_id_token: accessToken,
        };
        if (phone_number) {
          payload.phone_number = phone_number;
        }
        const { data } = await api.post("/otp-login", payload);
        setToken(data?.token);
        dispatch({ type: "SET_USER_DATA", data: data?.user });
        resolve(data);
      } catch (err: any) {
        const data = err.response?.data;
        const firstError =
          data?.errors && typeof data.errors === "object"
            ? Object.values(data.errors).flat()[0]
            : null;
        const raw =
          (typeof data?.message === "string" && data.message) ||
          (typeof data?.error === "string" && data.error) ||
          (Array.isArray(firstError) ? firstError[0] : firstError) ||
          err?.message;
        const message =
          (typeof raw === "string" && raw.trim()) || "تعذر الاتصال بالخادم";
        reject(data ? { ...data, message } : { message });
      }
    });
  };
